import { ThemeConfig, theme } from "antd";
import { RefineThemes } from "@refinedev/antd";

// Color palette - eMariner brand colors
export const colors = {
  // Brand colors (from emariner.net)
  primary: "#d84659",
  primaryHover: "#b41e30",
  primaryActive: "#f96173",

  // Sider colors (brand purple)
  siderBg: "#4E477F",
  siderBgDark: "#1D173B",

  // Semantic colors
  success: "#2e7d4a",
  successBg: "#f0f9f4",
  successBorder: "#8fd4a8",

  warning: "#d97706",
  warningBg: "#fffbeb",
  warningBorder: "#fcd34d",

  error: "#dc2626",
  errorBg: "#fef2f2",
  errorBorder: "#fca5a5",

  info: "#0369a1",
  infoBg: "#f0f9ff",
  infoBorder: "#7dd3fc",

  // Text hierarchy
  textPrimary: "#1a1a2e",
  textSecondary: "#4a4a68",
  textTertiary: "#8585a3",
  textDisabled: "#b3b3c6",
  text: "#4a4a68",
  white: "#ffffff",

  // Background hierarchy
  bgBase: "#ffffff",
  bgSubtle: "#fafafc",
  bgMuted: "#f4f4f8",
  bgLight: "#fafafa",
  bgHover: "#f5f5f5",

  // Border hierarchy
  borderLight: "#e8e8f0",
  borderDefault: "#d4d4e0",
  borderStrong: "#b3b3c6",

  // Purple container colors (brand purple tints for page sections)
  containerBg: "#f0eff5",
  containerBorder: "#c9c6db",
};

// Shadow system
export const shadows = {
  sm: "0 1px 2px rgba(0,0,0,0.03), 0 1px 6px -1px rgba(0,0,0,0.02), 0 2px 4px rgba(0,0,0,0.02)",
  md: "0 3px 6px -4px rgba(0,0,0,0.12), 0 6px 16px rgba(0,0,0,0.08), 0 9px 28px 8px rgba(0,0,0,0.05)",
  lg: "0 6px 16px -8px rgba(0,0,0,0.08), 0 9px 28px rgba(0,0,0,0.05), 0 12px 48px 16px rgba(0,0,0,0.03)",
  focus: "0 0 0 3px rgba(216, 70, 89, 0.12)",
};

// Light theme (extends RefineThemes.Red for eMariner brand)
export const lightTheme: ThemeConfig = {
  ...RefineThemes.Red,
  token: {
    // Core colors
    colorPrimary: colors.primary,
    colorSuccess: colors.success,
    colorWarning: colors.warning,
    colorError: colors.error,
    colorInfo: colors.primary,  // Use brand red for info (not blue)

    // Text colors
    colorText: colors.textSecondary,
    colorTextBase: colors.textPrimary,
    colorTextSecondary: colors.textTertiary,
    colorTextTertiary: colors.textDisabled,
    colorTextDescription: colors.textTertiary,
    colorTextPlaceholder: colors.textTertiary,
    colorTextDisabled: colors.textDisabled,

    // Link colors
    colorLink: colors.primary,
    colorLinkHover: colors.primaryHover,
    colorLinkActive: colors.primaryActive,

    // Background colors
    colorBgBase: colors.bgBase,
    colorBgContainer: colors.bgBase,
    colorBgLayout: colors.bgSubtle,

    // Border colors
    colorBorder: colors.borderDefault,
    colorBorderSecondary: colors.borderLight,
    colorSplit: colors.borderLight,

    // Border radius scale
    borderRadius: 8,
    borderRadiusSM: 6,
    borderRadiusLG: 12,
    borderRadiusXS: 4,

    // Typography
    fontFamily: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 13,
    fontSizeSM: 11,
    fontSizeLG: 15,
    fontSizeHeading1: 36,
    fontSizeHeading2: 32,
    fontSizeHeading3: 24,
    fontSizeHeading4: 20,
    fontSizeHeading5: 16,
    lineHeight: 1.6,
    fontWeightStrong: 600,

    // Shadows
    boxShadow: shadows.sm,
    boxShadowSecondary: shadows.md,

    // Focus states
    controlOutline: "rgba(216, 70, 89, 0.12)",
    controlOutlineWidth: 3,
  },
  components: {
    // Layout
    Layout: {
      siderBg: colors.siderBg,
      headerBg: colors.bgBase,
      bodyBg: colors.bgSubtle,
    },
    Menu: {
      darkItemBg: colors.siderBg,
      darkSubMenuItemBg: colors.siderBgDark,
      darkItemSelectedBg: colors.primary,
      darkItemSelectedColor: colors.white,
      itemBorderRadius: 8,
    },

    // Data Display
    Table: {
      headerBg: colors.bgMuted,
      headerColor: colors.textPrimary,
      headerSplitColor: colors.borderLight,
      rowHoverBg: "rgba(78, 71, 127, 0.04)",
      rowSelectedBg: "rgba(216, 70, 89, 0.08)",
      rowSelectedHoverBg: "rgba(216, 70, 89, 0.12)",
      borderColor: colors.borderLight,
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
    },
    Card: {
      headerBg: "transparent",
      headerFontSize: 16,
      actionsBg: colors.bgSubtle,
      borderRadiusLG: 12,
      paddingLG: 24,
    },
    Descriptions: {
      labelBg: colors.bgMuted,
      titleColor: colors.textPrimary,
      contentColor: colors.textSecondary,
    },
    Badge: {
      textFontWeight: 500,
    },

    // Form Inputs
    Input: {
      activeShadow: shadows.focus,
      paddingBlock: 8,
      paddingBlockLG: 10,
      paddingBlockSM: 4,
      paddingInline: 12,
      paddingInlineLG: 14,
      paddingInlineSM: 8,
      addonBg: colors.bgMuted,
    },
    InputNumber: {
      handleBg: colors.bgMuted,
      handleActiveBg: colors.bgHover,
      handleHoverColor: colors.primary,
    },
    Select: {
      optionSelectedBg: colors.containerBg,
      optionSelectedColor: colors.primary,
      optionActiveBg: colors.bgHover,
      multipleItemBg: colors.containerBg,
      multipleItemBorderColor: colors.containerBorder,
    },
    DatePicker: {
      activeShadow: shadows.focus,
      cellActiveWithRangeBg: "rgba(216, 70, 89, 0.1)",
      cellHoverBg: colors.bgHover,
      cellHoverWithRangeBg: "rgba(216, 70, 89, 0.06)",
    },
    Checkbox: {
      borderRadiusSM: 4,
    },
    Form: {
      labelColor: colors.textPrimary,
      itemMarginBottom: 20,
    },

    // Feedback
    Modal: {
      headerBg: colors.bgBase,
      contentBg: colors.bgBase,
      titleFontSize: 18,
      titleColor: colors.textPrimary,
      borderRadiusLG: 16,
    },
    Drawer: {
      colorBgElevated: colors.bgBase,
    },
    Alert: {
      colorInfoBg: colors.containerBg,        // Brand purple tint
      colorInfoBorder: colors.containerBorder, // Brand purple border
      colorSuccessBg: colors.successBg,
      colorSuccessBorder: colors.successBorder,
      colorWarningBg: colors.warningBg,
      colorWarningBorder: colors.warningBorder,
      colorErrorBg: colors.errorBg,
      colorErrorBorder: colors.errorBorder,
      borderRadiusLG: 10,
    },
    Message: {
      contentBg: colors.bgBase,
    },
    Notification: {
      width: 384,
    },

    // Navigation
    Tabs: {
      itemColor: colors.textSecondary,
      itemHoverColor: colors.primary,
      itemSelectedColor: colors.primary,
      itemActiveColor: colors.primary,
      inkBarColor: colors.primary,
      horizontalItemGutter: 32,
      horizontalItemPadding: "12px 0",
    },
    Steps: {
      colorPrimary: colors.primary,
    },
    Pagination: {
      itemActiveBg: colors.primary,
      itemActiveColorDisabled: colors.textDisabled,
      itemBg: "transparent",
      colorText: colors.textSecondary,
      colorPrimary: colors.white,
      borderRadius: 6,
    },
    // Actions
    Button: {
      primaryColor: colors.white,
      primaryShadow: "0 2px 0 rgba(216, 70, 89, 0.06)",
      defaultBorderColor: colors.borderDefault,
      defaultColor: colors.textSecondary,
      defaultShadow: "0 2px 0 rgba(0, 0, 0, 0.02)",
      controlHeight: 36,
      controlHeightLG: 44,
      controlHeightSM: 28,
    },
    Dropdown: {
      borderRadiusLG: 10,
      controlItemBgHover: colors.bgHover,
      controlItemBgActive: colors.containerBg,
    },

    // Other
    Tooltip: {
      colorBgSpotlight: colors.siderBgDark,
      colorTextLightSolid: colors.white,
    },
    Popover: {
      borderRadiusLG: 12,
    },
    Segmented: {
      itemColor: colors.textSecondary,
      itemHoverBg: colors.bgHover,
      itemSelectedBg: colors.bgBase,
      trackBg: colors.bgMuted,
      trackPadding: 4,
    },
    Progress: {
      defaultColor: colors.primary,
      remainingColor: colors.bgMuted,
    },
    Breadcrumb: {
      itemColor: colors.textTertiary,
      lastItemColor: colors.textSecondary,
      linkColor: colors.textSecondary,
      linkHoverColor: colors.primary,
      separatorColor: colors.textDisabled,
    },
  },
};

// Sider theme (always brand purple)
export const siderTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    colorBgContainer: colors.siderBgDark,
    colorText: colors.white,
    colorTextBase: colors.white,
    colorBgSpotlight: colors.siderBgDark,
  },
  components: {
    Layout: {
      siderBg: colors.siderBgDark,
    },
    Tooltip: {
      colorBgSpotlight: colors.siderBgDark,
      colorTextLightSolid: colors.white,
    },
    Menu: {
      colorBgContainer: colors.siderBgDark,
      itemBg: colors.siderBgDark,
      itemColor: colors.white,
      subMenuItemBg: colors.siderBgDark,
      popupBg: colors.siderBgDark,
      darkItemBg: colors.siderBgDark,
      darkSubMenuItemBg: colors.siderBgDark,
      darkPopupBg: colors.siderBgDark,
      darkItemSelectedBg: colors.primary,
      darkItemSelectedColor: colors.white,
      darkItemColor: colors.white,
      darkItemHoverColor: colors.white,
      darkItemHoverBg: "rgba(255, 255, 255, 0.15)",
    },
  },
};
